class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = [document.querySelector('#cart-icon-bubble'), document.querySelector('#cart-money-container')];

    cartLinks.forEach((cartLink) => {
      if (cartLink) { // Check if the element exists
        cartLink.setAttribute('role', 'button');
        cartLink.setAttribute('aria-haspopup', 'dialog');
        cartLink.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(cartLink);
        });
        cartLink.addEventListener('keydown', (event) => {
          if (event.code.toUpperCase() === 'SPACE') {
            event.preventDefault();
            this.open(cartLink);
          }
        });
      }
    });
  }


  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    
    // Refresh the drawer content when opening to ensure it's up to date
    // Only refresh if we haven't just updated it (to avoid unnecessary requests)
    if (!this._justUpdated) {
      this.refreshContent();
    } else {
      this._justUpdated = false;
    }
    
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        let containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : this.querySelector('#CartDrawer') || document.getElementById('CartDrawer');
        
        // Fallback to drawer__inner if CartDrawer is not found
        if (!containerToTrapFocusOn) {
          containerToTrapFocusOn = this.querySelector('.drawer__inner');
        }
        
        // Only trap focus if we have a valid container
        if (containerToTrapFocusOn) {
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Mark that we just updated so we don't refresh unnecessarily when opening
    this._justUpdated = true;
    
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }
    if (this.classList.contains('is-empty')) {
      this.classList.remove('is-empty');
    }
    this.productId = parsedState.id;
    
    // Debug logging
    console.log('renderContents called with:', {
      hasSections: !!parsedState.sections,
      sectionKeys: parsedState.sections ? Object.keys(parsedState.sections) : [],
      sectionsToRender: this.getSectionsToRender()
    });
    
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      
      if (!sectionElement) {
        console.warn(`Section element not found for:`, section);
        return;
      }
      
      if (!parsedState.sections || !parsedState.sections[section.id]) {
        console.warn(`Section data not found for:`, section.id, 'Available sections:', parsedState.sections ? Object.keys(parsedState.sections) : []);
        return;
      }
      
      const sectionHTML = parsedState.sections[section.id];
      const extractedHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
      
      console.log(`Updating section ${section.id}:`, {
        selector: section.selector,
        hasHTML: !!sectionHTML,
        extractedLength: extractedHTML.length,
        elementFound: !!sectionElement
      });
      
      if (extractedHTML) {
        sectionElement.innerHTML = extractedHTML;
      } else {
        console.error(`Failed to extract HTML for section ${section.id} with selector ${section.selector}`);
        // Fallback: try to use the raw HTML if extraction fails
        if (sectionHTML) {
          const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
          const shopifySection = parsed.querySelector('.shopify-section');
          if (shopifySection) {
            sectionElement.innerHTML = shopifySection.innerHTML;
            console.log('Used fallback: extracted .shopify-section innerHTML');
          }
        }
      }
    });

    // Wait for custom elements to be upgraded and DOM to be ready
    // Use multiple requestAnimationFrame calls to ensure everything is processed
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Re-attach event listeners that may have been removed
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) {
          overlay.addEventListener('click', this.close.bind(this));
        }
        
        // Ensure custom elements are defined (they should auto-upgrade, but ensure it)
        if (customElements.get('cart-drawer-items')) {
          // Force upgrade any cart-drawer-items elements
          const drawerItems = this.querySelectorAll('cart-drawer-items');
          drawerItems.forEach(item => {
            if (!item.isConnected || !item.shadowRoot) {
              // Element should auto-upgrade, but ensure it's processed
            }
          });
        }
        
        this.open();
      });
    });
  }

  async refreshContent() {
    try {
      // Fetch the cart drawer section with current cart state
      // Use the sections parameter format that Shopify expects
      const sectionsToFetch = this.getSectionsToRender().map(section => section.id).join(',');
      const url = `/?sections=${sectionsToFetch}`;
      
      const response = await fetch(url);
      if (!response.ok) {
        console.warn('Failed to fetch cart drawer sections');
        return;
      }
      
      const text = await response.text();
      let sectionsData;
      
      // Try to parse as JSON first (sections API returns JSON)
      try {
        sectionsData = JSON.parse(text);
      } catch (e) {
        // If not JSON, parse as HTML
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        sectionsData = {};
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = parsed.querySelector(`[data-section-id="${section.id}"]`);
          if (sectionElement) {
            sectionsData[section.id] = sectionElement.outerHTML;
          }
        });
      }
      
      // Update each section
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        
        if (!sectionElement || !sectionsData[section.id]) return;
        
        const sectionHTML = sectionsData[section.id];
        const extractedHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
        
        if (extractedHTML) {
          sectionElement.innerHTML = extractedHTML;
        }
      });
      
      // Update empty state based on cart items
      const drawerInner = this.querySelector('.drawer__inner');
      const cartItems = this.querySelector('#CartDrawer-CartItems');
      if (drawerInner) {
        const hasItems = cartItems && cartItems.querySelector('.cart-item');
        if (hasItems) {
          drawerInner.classList.remove('is-empty');
          this.classList.remove('is-empty');
        } else {
          drawerInner.classList.add('is-empty');
          this.classList.add('is-empty');
        }
      }
    } catch (error) {
      console.error('Error refreshing cart drawer content:', error);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    let element = null;
    
    // Strategy 1: Try to find the selector directly
    element = parsed.querySelector(selector);
    
    // Strategy 2: If not found, try within .shopify-section wrapper
    if (!element) {
      const shopifySection = parsed.querySelector('.shopify-section');
      if (shopifySection) {
        element = shopifySection.querySelector(selector);
      }
    }
    
    // Strategy 3: If selector is #CartDrawer, try finding it within cart-drawer element
    if (!element && selector === '#CartDrawer') {
      const cartDrawerElement = parsed.querySelector('cart-drawer');
      if (cartDrawerElement) {
        element = cartDrawerElement.querySelector(selector);
      }
      // Also try direct query in case cart-drawer is not the wrapper
      if (!element) {
        element = parsed.querySelector(selector);
      }
    }
    
    // Strategy 4: For other selectors, try within any parent container
    if (!element && selector.startsWith('#')) {
      // Try to find by ID anywhere in the document
      element = parsed.getElementById(selector.substring(1));
    }
    
    // If we found the element, return its innerHTML
    if (element) {
      return element.innerHTML;
    }
    
    // Fallback: if selector is .shopify-section, return its innerHTML
    if (selector === '.shopify-section') {
      const shopifySection = parsed.querySelector('.shopify-section');
      if (shopifySection) {
        return shopifySection.innerHTML;
      }
    }
    
    console.warn('getSectionInnerHTML: Could not find element with selector:', selector);
    return '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'cart-total',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-total',
        section: 'cart-total',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
